import type {HubConnection} from '@microsoft/signalr';
import {HubConnectionBuilder, LogLevel} from '@microsoft/signalr'
import {useRuntimeConfig} from '#app'
import {MessagePackHubProtocol} from "@microsoft/signalr-protocol-msgpack";

let connection: HubConnection | null = null

export const createSignalRConnection = async () => {
  if (connection) {
    return connection
  }

  const config = useRuntimeConfig().public.signalR
  const retryInterval = 30000;
  let logLevel = LogLevel.Error;
  let url: URL | null = null

  try {
    url = new URL(config.url)
    url.pathname = `${config.hubName}`
  } catch (e) {
    console.error(e)
    throw new Error(`SignalR URL is undefined or invalid: ${config.url}`)
  }

  if (!url)
    throw new Error('SignalR URL is undefined')

  if (process.env.NODE_ENV === 'production')
    logLevel = LogLevel.Critical

  connection = new HubConnectionBuilder()
    .withUrl(url.toString(), {
      accessTokenFactory: async () => (await $fetch('/api/signalr/connection')).token
    })
    .configureLogging(logLevel)
    .withHubProtocol(new MessagePackHubProtocol())
    .withAutomaticReconnect({
      nextRetryDelayInMilliseconds: context => {
        // Retry immediately for the first attempt (context.previousRetryCount === 0)
        if (context.previousRetryCount === 0) return 0;

        // Retry every 30 seconds after the first attempt
        return retryInterval;
      }
    })
    .build()

  try {
    await connection.start()
  } catch (error) {
    connection = null
    throw error
  }

  return connection
}

export const getSignalRConnection = () => connection

export const stopSignalRConnection = async () => {
  const activeConnection = connection
  connection = null
  await activeConnection?.stop()
}

export const on = (methodName: string, newMethod: (...args: unknown[]) => void) => {
  connection?.on(methodName, newMethod)
}

export const off = (methodName: string, method: (...args: unknown[]) => void) => {
  connection?.off(methodName, method)
}

export const invoke = async (methodName: string, ...args: unknown[]) => {
  if (connection) {
    return await connection.invoke(methodName, ...args)
  } else {
    throw new Error('SignalR connection not established')
  }
}